import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Grid, IconButton, Box, Typography, Button, CircularProgress, Card, CardContent, Avatar, Divider, Chip } from '@mui/material';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast, Zoom } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {
    Users,
    UserCheck,
    Calendar,
    Clock,
    ChevronRight,
    TrendingUp,
    Activity,
    User,
    UserPlus,
    PieChart as PieChartIcon,
    Filter,
    AlertCircle
} from 'lucide-react';

import { kioskModel, teleConsultationModel } from './Axios/AxiosConfig';
import { getDynamicLogo } from './getDynamicLogo';
import AppointmentStore from './DataRepository/AppointmentStore';

dayjs.extend(isBetween);

function Main() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [patients, setPatients] = useState([]);
    const [isLast30Days, setIsLast30Days] = useState(false);

    const kiosk_detailsCookie = Cookies.get('kiosk_details');
    let kiosk_details = {};
    if (kiosk_detailsCookie) {
        try {
            kiosk_details = JSON.parse(kiosk_detailsCookie);
        } catch (error) {
            console.log('Error parsing kiosk_details cookie:', error);
        }
    }

    const doctorDetailsCookie = Cookies.get('Doctor_details');
    let Doctor_details = {};
    if (doctorDetailsCookie) {
        try {
            Doctor_details = JSON.parse(doctorDetailsCookie);
        } catch (error) {
            console.log('Error parsing Doctor_details cookie:', error);
        }
    }

    const calculateAge = (dob) => {
        if (!dob) return 0;
        let day, month, year;
        if (dob.includes('/')) {
            [day, month, year] = dob.split('/').map(part => parseInt(part, 10));
        } else if (dob.length === 8) {
            day = parseInt(dob.substring(0, 2), 10);
            month = parseInt(dob.substring(2, 4), 10);
            year = parseInt(dob.substring(4, 8), 10);
        } else {
            return 0;
        }
        const dobDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - dobDate.getFullYear();
        const hasBirthdayPassed = today.getMonth() > dobDate.getMonth() || (today.getMonth() === dobDate.getMonth() && today.getDate() >= dobDate.getDate());
        if (!hasBirthdayPassed) age--;
        return age;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            // 1. Try fetching today's data
            const today = dayjs().format('YYYY-MM-DD');
            const todayRes = await kioskModel.post('getClinicalKioskReportbyDate', {
                date: today,
                kiosk_id: kiosk_details?.kiosk_id,
            });

            if (todayRes.status === 200 && todayRes.data.length > 0) {
                setPatients(todayRes.data);
                setIsLast30Days(false);
            } else {
                // 2. Fallback: Fetch all patients and filter for last 30 days
                const allPatientsRes = await kioskModel.post('/getAllClinicalPatients', {
                    kiosk_id: kiosk_details?.kiosk_id,
                });

                if (allPatientsRes.status === 200) {
                    const thirtyDaysAgo = dayjs().subtract(30, 'day');
                    const filtered = (allPatientsRes.data || []).filter(p => {
                        const createdDate = dayjs(p.created_at);
                        return createdDate.isAfter(thirtyDaysAgo) && createdDate.isBefore(dayjs().add(1, 'day'));
                    });
                    setPatients(filtered);
                    setIsLast30Days(true);
                }
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            toast.error("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const analytics = useMemo(() => {
        const total = patients.length;
        let male = 0, female = 0, other = 0;
        let child = 0, adult = 0, senior = 0;

        patients.forEach(p => {
            // Gender distribution
            const gender = p.gender?.toLowerCase();
            if (gender === 'male') male++;
            else if (gender === 'female') female++;
            else other++;

            // Age distribution
            const age = calculateAge(p.dob);
            if (age < 18) child++;
            else if (age <= 60) adult++;
            else senior++;
        });

        return {
            total,
            gender: { male, female, other },
            age: { child, adult, senior }
        };
    }, [patients]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <div style={{ position: "relative", width: "10rem", height: "10rem" }}>
                    <CircularProgress style={{ width: "100%", height: "100%", color: "var(--themePrimaryColor, #DA6E0B)" }} thickness={1} />
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <img src={getDynamicLogo()} style={{ width: "80%", height: "80%", objectFit: "contain" }} alt="Loading" />
                    </div>
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, backgroundColor: 'var(--themeSurfaceColor, #FDFDFD)', minHeight: '100%' }}>
            {/* Header & Notification */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#3B362A', letterSpacing: '-0.5px', fontFamily: "'Poppins', sans-serif" }}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#75623F', mt: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                        Overview of kiosk activities and patient list.
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to="/AddUser"
                    variant="contained"
                    startIcon={<UserPlus size={18} />}
                    sx={{
                        bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                        '&:hover': { bgcolor: '#B85C09' },
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.5,
                        fontWeight: 700,
                        fontFamily: "'Poppins', sans-serif",
                        boxShadow: '0 8px 16px rgba(218, 110, 11, 0.15)'
                    }}
                >
                    Add Patient
                </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
                {isLast30Days && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#FFF5F5',
                        border: '1px solid #FEB2B2',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <AlertCircle size={20} color="#E53E3E" />
                        <Typography variant="body2" sx={{ color: '#C53030', fontWeight: 500 }}>
                            No data available for today. Showing records from the last 30 days.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Analytics Cards */}
            <Grid container spacing={3} sx={{ mb: 6 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <AnalyticsCard
                        title="Total Patients"
                        value={analytics.total}
                        icon={<Users color="var(--themePrimaryColor, #DA6E0B)" />}
                        gradient="linear-gradient(135deg, var(--themeSecondaryColor, #FFF4E5) 0%, var(--themeSecondaryColor, #F4D2B3) 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnalyticsCard
                        title="Male / Female"
                        value={`${analytics.gender.male} / ${analytics.gender.female}`}
                        icon={<User color="var(--themeSecondaryColor, #75623F)" />}
                        gradient="linear-gradient(135deg, var(--themeSecondaryColor, #F1EFEC) 0%, var(--themeSecondaryColor, #C0B7A7) 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnalyticsCard
                        title="Adults (18-60)"
                        value={analytics.age.adult}
                        icon={<Activity color="#918165" />}
                        gradient="linear-gradient(135deg, #FDF7F2 0%, #EEBC8F 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AnalyticsCard
                        title="Seniors (>60)"
                        value={analytics.age.senior}
                        icon={<TrendingUp color="#A3967E" />}
                        gradient="linear-gradient(135deg, #F9F7F4 0%, #D4CEB0 100%)"
                    />
                </Grid>
            </Grid>

            {/* Patient Listing */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D3748' }}>
                        {isLast30Days ? "Recent Patient Profiles" : "Today's Patient Profiles"}
                    </Typography>
                    <Button
                        component={Link}
                        to="/Patientlist"
                        endIcon={<ChevronRight size={18} />}
                        sx={{ color: 'var(--themePrimaryColor, #DA6E0B)', textTransform: 'none', fontWeight: 600 }}
                    >
                        View All
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {patients.length > 0 ? (
                        patients.map((patient, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <PatientProfileCard patient={patient} calculateAge={calculateAge} navigate={navigate} />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box sx={{
                                p: 8,
                                textAlign: 'center',
                                backgroundColor: '#f8fafc',
                                borderRadius: '20px',
                                border: '2px dashed #e2e8f0'
                            }}>
                                <img src="/icons/NoAppointment.png" alt="No data" style={{ width: '200px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ color: '#64748b' }}>No Patient Profiles Found</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    );
}

const AnalyticsCard = ({ title, value, icon, gradient }) => (
    <Card sx={{
        height: '100%',
        borderRadius: '16px',
        boxShadow: 'none',
        border: '1px solid #E2E8F0',
        background: gradient
    }}>
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    display: 'flex',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                }}>
                    {icon}
                </Box>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--themePrimaryColor, #3B362A)', mb: 0.5 }}>
                {value}
            </Typography>
            <Typography variant="body2" sx={{ color: '#575248', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {title}
            </Typography>
        </CardContent>
    </Card>
);

const PatientProfileCard = ({ patient, calculateAge, navigate }) => {
    const age = calculateAge(patient.dob);

    return (
        <Card sx={{
            borderRadius: '20px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
            border: '1px solid #f1f5f9',
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                borderColor: 'var(--themePrimaryColor, #DA6E0B)'
            }
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: 'var(--themeSecondaryColor, #F1EFEC)',
                            color: 'var(--themePrimaryColor, #DA6E0B)',
                            fontSize: '24px',
                            fontWeight: 700,
                            border: '2px solid #fff',
                            boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                        }}
                    >
                        {patient.firstname?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--themePrimaryColor, #3B362A)', mb: 0.5 }}>
                            {patient.firstname} {patient.lastname}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                label={`${age} Yrs`}
                                size="small"
                                sx={{ bgcolor: 'var(--themeSecondaryColor, #F4D2B3)', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, borderRadius: '8px' }}
                            />
                            <Chip
                                label={patient.gender}
                                size="small"
                                sx={{ bgcolor: 'var(--themeSecondaryColor, #E7E7DB)', color: 'var(--themePrimaryColor, #312110)', fontWeight: 600, borderRadius: '8px' }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Calendar size={16} color="#918165" />
                        <Typography variant="body2" sx={{ color: '#575248', fontWeight: 500 }}>
                            Created: {dayjs(patient.created_at).format('DD MMM, YYYY')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Clock size={16} color="#918165" />
                        <Typography variant="body2" sx={{ color: '#575248', fontWeight: 500 }}>
                            Last Visit: {dayjs(patient.updated_at).format('DD MMM, YYYY')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <User size={16} color="#918165" />
                        <Typography variant="body2" sx={{ color: '#575248', fontWeight: 500 }}>
                            UID: {patient.UID || '–'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Activity size={16} color="#918165" />
                        <Typography variant="body2" sx={{ color: '#575248', fontWeight: 500 }}>
                            Kiosk: {patient.kiosk_name || '–'}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(`/patient/${patient.UID}`)}
                    sx={{
                        bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                        '&:hover': { bgcolor: '#B85C09' },
                        textTransform: 'none',
                        borderRadius: '12px',
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: '0 4px 6px -1px rgba(218, 110, 11, 0.2)'
                    }}
                >
                    View Full Profile
                </Button>
            </CardContent>
        </Card>
    );
};

export default Main;
